import { Link } from 'react-router-dom';

interface ComicCardProps {
  id: number;
  slug: string;
  title: string;
  author: string;
  category: string;
  categorySlug: string;
  status: string;
  rating: number;
  views: number;
  chapters: number;
  coverImage: string;
  tags: string[];
  featured?: boolean;
}

export default function ComicCard({ comic, featured }: { comic: ComicCardProps; featured?: boolean }) {
  const formatViews = (views: number) => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  if (featured) {
    return (
      <Link
        to={`/comic/${comic.slug}`}
        className="group block bg-background-200/40 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/70"
      >
        <div className="flex flex-col sm:flex-row gap-0">
          <div className="relative sm:w-[45%] shrink-0 overflow-hidden">
            <div className="aspect-[3/4] sm:aspect-[3/4] w-full">
              <img
                src={comic.coverImage}
                alt={comic.title}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500/90 text-background-950 text-xs font-medium whitespace-nowrap">
                <i className="ri-fire-fill text-xs"></i>
                推荐
              </span>
            </div>
          </div>
          <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
            <div>
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50 group-hover:text-primary-400 transition-colors">
                {comic.title}
              </h3>
              <p className="mt-1 text-xs text-foreground-500">
                {comic.author} · {comic.category}
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className="flex items-center gap-1 text-sm text-primary-400 font-semibold">
                  <i className="ri-star-fill text-sm"></i>
                  {comic.rating}
                </span>
                <span className="text-xs text-foreground-600">{formatViews(comic.views)} 阅读</span>
                <span className="text-xs text-foreground-600">{comic.chapters} 话</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                {comic.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-[10px] rounded-full bg-background-300/40 text-foreground-400 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
                <span
                  className={`px-2 py-0.5 text-[10px] rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-500/20 text-accent-400'
                      : 'bg-secondary-500/20 text-secondary-400'
                  }`}
                >
                  {comic.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.slug}`}
      className="group block bg-background-200/30 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/60 hover:-translate-y-1"
    >
      <div className="relative overflow-hidden">
        <div className="aspect-[3/4] w-full">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="absolute top-2.5 left-2.5">
          <span
            className={`inline-block px-2 py-0.5 text-[10px] rounded-full whitespace-nowrap ${
              comic.status === '连载中'
                ? 'bg-accent-500/80 text-background-950'
                : 'bg-secondary-500/80 text-background-950'
            }`}
          >
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2.5 right-2.5">
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-background-950/70 text-primary-400 text-[10px] font-semibold whitespace-nowrap">
            <i className="ri-star-fill text-[10px]"></i>
            {comic.rating}
          </span>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-heading font-bold text-foreground-50 group-hover:text-primary-400 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="mt-1 text-[11px] text-foreground-500 line-clamp-1">
          {comic.author}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[11px] text-foreground-600">{comic.chapters} 话</span>
          <span className="text-foreground-700">·</span>
          <span className="text-[11px] text-foreground-600">{formatViews(comic.views)}</span>
        </div>
      </div>
    </Link>
  );
}